import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  CloseButton,
  createStyles,
  Divider,
  FileButton,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useRef} from 'react';

import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import EventTriggerConfigForm from './EventTriggerConfigForm';
import useFolderScopeAccess, {FolderScopeAccessStatus} from './eventTriggerConfigForm/useFolderScopeAccess';
import RoutineAccessSection from './RoutineAccessSection';
import RoutineFieldHeader from './RoutineFieldHeader';
import {canSaveRoutine, isTriggerComplete} from './routineFormValidation';
import RoutineRunAsSelector from './RoutineRunAsSelector';
import {
  AllowedEventTrigger,
  EventTriggerConfig,
  RoutineRunAs,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import TimeTriggerConfigForm from './TimeTriggerConfigForm';
import TriggerTypeSelector from './TriggerTypeSelector';
import {AccessLevel, getRunAsSelectorValue, RoutineFormState} from './useRoutineForm';

interface RoutineFormProps {
  isEdit: boolean;
  formState: RoutineFormState;
  savedFolderIds: Array<string>;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onRemoveFileBlock: (index: number) => void;
  onAddFile: (file: File) => void;
  isUploading: boolean;
  onTriggerTypeChange: (value: TriggerType) => void;
  onTriggerConfigChange: (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => void;
  onAccessLevelChange: (level: AccessLevel) => void;
  onAgentWorkspaceFolderChange: (folderId: string | null) => void;
  onMemoryEnabledChange: (enabled: boolean) => void;
  onRunAsChange: (value: RoutineRunAs) => void;
  onCopyRequest: () => void;
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onArchive?: () => void;
  onViewAuditLog?: () => void;
  onViewMemory?: () => void;
  currentStatus?: RoutineStatus;
  currentRunAs?: RoutineRunAs;
  canArchive?: boolean;
  canEdit?: boolean;
  canPause?: boolean;
  canResume?: boolean;
  allowedEventTriggers: Array<AllowedEventTrigger>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  form: {
    height: '100%',
  },
  nameInput: {
    minHeight: '3.25rem',
  },
  linkAction: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    fontWeight: theme.other.fontWeights.semibold,
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  formFooter: {
    paddingTop: theme.spacing.md,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
  formBody: {
    border: 0,
    margin: 0,
    minWidth: 0,
    paddingTop: theme.spacing.xxl,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.colors.gray[4],
      borderRadius: theme.radius.sm,
    },
    '&::-webkit-scrollbar-track': {
      background: theme.colors.gray[0],
    },
  },
}));

function getLocationScopeAlert(
  accessStatus: FolderScopeAccessStatus
): {color: 'blue' | 'yellow'; message: string} | null {
  switch (accessStatus) {
    case 'loading':
      return {
        color: 'blue',
        message: 'Checking project and folder access before enabling editing...',
      };
    case 'inaccessible':
      return {
        color: 'yellow',
        message:
          "This agent can't be edited because you don't have access to all of its configured projects or folders.",
      };
    case 'error':
      return {
        color: 'yellow',
        message:
          "This agent can't be edited because project and folder access couldn't be verified. Refresh and try again.",
      };
    case 'accessible':
      return null;
    default:
      return null;
  }
}

export default function RoutineForm({
  isEdit,
  formState,
  savedFolderIds,
  onNameChange,
  onDescriptionChange,
  onPromptChange,
  onRemoveFileBlock,
  onAddFile,
  isUploading,
  onTriggerTypeChange,
  onTriggerConfigChange,
  onAccessLevelChange,
  onAgentWorkspaceFolderChange,
  onMemoryEnabledChange,
  onRunAsChange,
  isDirty,
  isSaving,
  error,
  onSave,
  onCancel,
  onPause,
  onResume,
  onArchive,
  onViewAuditLog,
  onCopyRequest,
  onViewMemory,
  currentStatus,
  currentRunAs,
  canArchive = false,
  canEdit = false,
  canPause = false,
  canResume = false,
  allowedEventTriggers,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineFormProps): JSX.Element {
  const {classes} = useStyles();
  const [archiveModalOpened, {open: openArchiveModal, close: closeArchiveModal}] = useDisclosure(false);
  const resetFileInputRef = useRef<() => void>(null);

  // Full access has three states: hidden (not released), locked (released but not enabled), and
  // active (enabled). The two agent-approval configs drive release vs enable. The section is also
  // withheld until the trigger config is complete, so agent mode is chosen last.
  const {llmEnableAgentMemory, llmEnableRoutineServiceAccountUi} = useRolloutFlags();
  const showRunAsSection = llmEnableRoutineServiceAccountUi === true;
  const serviceAccountOptionEnabled = BenchlingConfig.LLM_ENABLE_ROUTINES_RUN_AS_SERVICE_ACCOUNT;
  const fullAccessEnabled = BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS;
  // Enabling implies release, so treat an enabled tenant as visible even if the release flag lags.
  const agentApprovalsVisible = BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED || fullAccessEnabled;
  const showAccessSection = agentApprovalsVisible && isTriggerComplete(formState);

  const isTimeBased = formState.triggerType === TriggerType.TIME_BASED;
  const {folders: locationScopeFolders, status: locationScopeAccessStatus} =
    useFolderScopeAccess(savedFolderIds);
  const hasSavedFolderScope = isEdit && savedFolderIds.length > 0;
  const locationScopeAlert = hasSavedFolderScope ? getLocationScopeAlert(locationScopeAccessStatus) : null;
  const locationScopeActivationDisabled = hasSavedFolderScope && locationScopeAccessStatus !== 'accessible';
  const writeDisabled = isEdit && (!canEdit || locationScopeActivationDisabled);

  const canSave = canSaveRoutine({
    formState,
    isEdit,
    isDirty,
    isSaving,
    isUploading,
    currentRunAs,
  });

  return (
    <Stack spacing="0" className={classes.form}>
      <Box component="fieldset" className={classes.formBody} disabled={writeDisabled}>
        <Stack spacing="md">
          {formState.banner}
          {locationScopeAlert != null && (
            <Alert color={locationScopeAlert.color} variant="light" role="status">
              {locationScopeAlert.message}
            </Alert>
          )}
          <TextInput
            label="Name"
            classNames={{input: classes.nameInput}}
            value={formState.name}
            onChange={(e) => {
              onNameChange(e.currentTarget.value);
            }}
          />

          <Textarea
            label="Description"
            description="A summary of what this Agent does — this will not be sent along to the agent."
            placeholder="optional"
            minRows={2}
            autosize={true}
            maxRows={8}
            value={formState.description}
            onChange={(e) => {
              onDescriptionChange(e.currentTarget.value);
            }}
          />

          <Textarea
            label="Instructions"
            description="The prompt that will be sent to the agent when this Agent runs."
            minRows={4}
            autosize={true}
            maxRows={16}
            value={formState.prompt}
            onChange={(e) => {
              onPromptChange(e.currentTarget.value);
            }}
          />

          <Stack spacing="sm" pb="xs">
            <RoutineFieldHeader
              title="Attachments"
              description="Attach files to provide additional context to the agent when this Agent runs."
            />
            {formState.fileBlocks.length > 0 && (
              <Group spacing="xs">
                {formState.fileBlocks.map((fileBlock, index) => (
                  <Badge
                    key={`${fileBlock.external_file_id ?? fileBlock.s3_location}-${index}`}
                    variant="outline"
                    size="sm"
                    rightSection={
                      <CloseButton
                        aria-label={`Remove ${fileBlock.filename}`}
                        size="xs"
                        onClick={() => {
                          onRemoveFileBlock(index);
                        }}
                      />
                    }
                  >
                    {fileBlock.filename}
                  </Badge>
                ))}
              </Group>
            )}
            <FileButton
              resetRef={resetFileInputRef}
              onChange={(file) => {
                if (file != null) {
                  onAddFile(file);
                }
                // Reset so selecting the same file again re-triggers onChange.
                resetFileInputRef.current?.();
              }}
            >
              {(props) => (
                <Anchor
                  {...props}
                  component="button"
                  type="button"
                  className={classes.linkAction}
                  disabled={isUploading}
                >
                  <BenchlingIcon icon="plus" />
                  {isUploading ? 'Uploading…' : 'Add file'}
                </Anchor>
              )}
            </FileButton>
          </Stack>

          {llmEnableAgentMemory === true && (
            <>
              <Divider />
              <Stack spacing="sm" pb="xs">
                <Group position="apart" align="flex-start" noWrap={true}>
                  <RoutineFieldHeader
                    title="Memory"
                    description="Let this Agent recall and update durable knowledge across separate runs."
                  />
                  <Switch
                    pt="xs"
                    aria-label="Enable Agent memory"
                    checked={formState.memoryEnabled}
                    onChange={(event) => {
                      onMemoryEnabledChange(event.currentTarget.checked);
                    }}
                  />
                </Group>
                {onViewMemory != null && (
                  <Anchor
                    component="button"
                    type="button"
                    className={classes.linkAction}
                    onClick={onViewMemory}
                  >
                    View memory
                    <BenchlingIcon icon="chevron-right" />
                  </Anchor>
                )}
              </Stack>
            </>
          )}

          <Divider />

          <TriggerTypeSelector
            value={formState.triggerType}
            onChange={onTriggerTypeChange}
            timeBasedEnabled={timeBasedEnabled}
            eventBasedEnabled={eventBasedEnabled}
            disabled={isEdit || writeDisabled}
          />

          {formState.triggerType != null &&
            (isTimeBased ? (
              <TimeTriggerConfigForm
                config={formState.triggerConfig as Partial<TimeTriggerConfig>}
                onChange={onTriggerConfigChange}
              />
            ) : (
              <EventTriggerConfigForm
                config={formState.triggerConfig as Partial<EventTriggerConfig>}
                onChange={onTriggerConfigChange}
                allowedTriggers={allowedEventTriggers}
                locationScopeAccessStatus={locationScopeAccessStatus}
                locationScopeFolders={locationScopeFolders}
              />
            ))}

          {showRunAsSection && (
            <>
              <Divider />
              <RoutineRunAsSelector
                value={getRunAsSelectorValue(formState)}
                onChange={onRunAsChange}
                disabled={writeDisabled}
                serviceAccountOptionDisabled={!serviceAccountOptionEnabled}
                currentRunAs={currentRunAs}
                onCopyRequest={onCopyRequest}
              />
            </>
          )}

          {showAccessSection && (
            <>
              <Divider />
              <RoutineAccessSection
                allowedApprovalCategories={formState.allowedApprovalCategories}
                agentWorkspaceFolderId={formState.agentWorkspaceFolderId}
                fullAccessEnabled={fullAccessEnabled}
                onAccessLevelChange={onAccessLevelChange}
                onAgentWorkspaceFolderChange={onAgentWorkspaceFolderChange}
              />
            </>
          )}

          {error != null && (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      {isEdit && (
        <Group position="apart" pt="md" className={classes.formFooter}>
          <Group spacing="xs">
            {canArchive && onArchive && (
              <Button variant="warn" size="sm" onClick={openArchiveModal} disabled={isSaving}>
                Archive
              </Button>
            )}
            <Group spacing="xs">
              {isEdit && currentStatus === RoutineStatus.ACTIVE && onPause && (
                <Button variant="secondary" size="sm" onClick={onPause} disabled={isSaving || !canPause}>
                  Pause
                </Button>
              )}
              {isEdit && currentStatus === RoutineStatus.PAUSED && onResume && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onResume}
                  disabled={isSaving || !canResume || locationScopeActivationDisabled}
                >
                  Resume
                </Button>
              )}
              {onViewAuditLog && (
                <Button variant="ghost" size="sm" onClick={onViewAuditLog}>
                  {EXPORT_AUDIT_LOG_DISPLAY_STRING}
                </Button>
              )}
            </Group>
          </Group>
          <Group spacing="xs">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              {!writeDisabled ? 'Cancel' : 'Back'}
            </Button>
            {!writeDisabled && (
              <Button variant="primary" size="sm" onClick={onSave} disabled={!canSave} loading={isSaving}>
                Save
              </Button>
            )}
          </Group>
        </Group>
      )}

      <Modal
        opened={archiveModalOpened}
        onClose={closeArchiveModal}
        title="Archive Agent"
        centered={true}
        closeOnClickOutside={true}
        zIndex={ZINDEX_FLOATING_UI}
      >
        <Stack spacing="md">
          <Text size="sm">Are you sure? This will stop and remove this Agent.</Text>
          <Group position="right" spacing="xs">
            <Button variant="ghost" size="sm" onClick={closeArchiveModal}>
              Cancel
            </Button>
            <Button
              variant="warn"
              size="sm"
              onClick={() => {
                closeArchiveModal();
                onArchive?.();
              }}
            >
              Archive
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
